/**
 * Builds the request payload for the `sendWorldEmail` cloud function for a
 * manual inline reply.
 *
 * This mirrors the payload shape produced by the legacy EmailAiModes/
 * InboxModeEmail send path (so the backend behaves identically), but is a
 * small pure function instead of being buried inside a 1.7k-line component.
 *
 * It deliberately does NOT call any network/Firestore APIs — footer
 * resolution happens in the caller (useMailSend) so this stays testable.
 */

import { convertToHtml } from '../../EmailFooterGenerator';
import { splitHtmlQuote, splitTextQuote } from './quoteSplitter';

/**
 * Appends the original message as a quoted conversation history block,
 * matching the markup the classic reply flow produces.
 *
 * @param {string} htmlContent already footer-decorated HTML
 * @param {object} originalEmail { from, date, content }
 * @returns {string}
 */
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Converts a plain-text email body to HTML that renders identically in EVERY
 * client (incl. the Word-based Outlook desktop, which ignores
 * `white-space: pre-wrap`). We normalise line endings, escape HTML, then turn
 * newlines into hard <br> — so line breaks survive regardless of CSS support.
 * The surrounding `white-space: pre-wrap` still preserves runs of spaces in
 * clients that honour it.
 */
const textToHtml = (s) =>
  escapeHtml(String(s).replace(/\r\n?/g, '\n')).replace(/\n/g, '<br>');

const appendHistory = (htmlContent, originalEmail) => {
  if (!originalEmail) return htmlContent;
  const when = new Date(
    originalEmail.date || originalEmail.timestamp || Date.now()
  ).toLocaleString();
  const who = originalEmail.from || 'Unknown';

  // Quote only the message we're replying to — its OWN new content, not the
  // whole nested chain it already carries (otherwise every reply piles the
  // entire conversation on top of itself). We strip the original's embedded
  // quote via the same splitter the UI uses.
  //
  // Prefer the original's HTML so its styling (paragraphs, formatting, inline
  // images) is carried over verbatim. Only when no HTML exists do we fall back
  // to the plain-text body — and then we preserve its line breaks via
  // `white-space: pre-wrap` so it never collapses into one run-on blob.
  const htmlBody = originalEmail.content || originalEmail.body || '';
  let body;
  if (htmlBody) {
    const { mainHtml } = splitHtmlQuote(htmlBody);
    body = mainHtml || htmlBody;
  } else {
    const { mainText } = splitTextQuote(originalEmail.bodyText || '');
    const plain = mainText || originalEmail.bodyText || '';
    body = `<div style="white-space: pre-wrap;">${escapeHtml(plain)}</div>`;
  }

  const history = `
<div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #666;">
  <p style="margin-bottom: 10px; color: #888; font-style: italic;">Am ${when} schrieb ${who}:</p>
  <blockquote style="margin: 0; padding-left: 10px; border-left: 2px solid #ddd; color: #666;">
    ${body}
  </blockquote>
</div>`;

  return `${htmlContent}\n\n${history}`;
};

/**
 * @param {object} args
 * @param {string} args.userId
 * @param {object} args.account     sender account doc (email, password, imap/smtp…)
 * @param {string} args.plainText   the user-typed reply (plain text)
 * @param {string} args.subject
 * @param {string[]} args.recipients
 * @param {object} args.originalEmail original message for quoting + leadId
 * @param {object} args.footerSettings resolved footer settings
 * @param {string|null} args.footerId resolved footer id
 * @param {string} [args.leadId]
 * @param {Array<object>} [args.attachments] wire-format attachment metadata
 *   ({ filename, storagePath, contentType, size }) — the backend pulls the
 *   actual bytes from Storage right before send.
 * @returns {object} ready-to-POST request body
 */
export const buildReplyPayload = ({
  userId,
  account,
  plainText,
  subject,
  recipients,
  originalEmail,
  footerSettings = {},
  footerId = null,
  leadId = null,
  attachments = [],
  cc = [],
  bcc = [],
  isNewEmail = false,
}) => {
  // convertToHtml turns the plain reply into HTML and bakes in the footer.
  const htmlWithFooter = convertToHtml(plainText || '', footerSettings, footerId);
  const content = appendHistory(htmlWithFooter, originalEmail);

  return {
    recipients: recipients.filter(Boolean),
    cc: (Array.isArray(cc) ? cc : []).filter(Boolean),
    bcc: (Array.isArray(bcc) ? bcc : []).filter(Boolean),
    subject: subject || '',
    content,
    userId,
    businessName: isNewEmail ? 'Neue E-Mail' : 'E-Mail-Antwort',
    leadId: leadId || originalEmail?.id || originalEmail?.leadId || null,
    emailType: isNewEmail ? 'new_blank_email' : 'custom_reply',
    senderEmail: (account.email || '').trim(),
    senderPassword: account.password,
    senderName: account.sender_name || 'BonusClub',
    imapHost: account.imap_host,
    imapPort: account.imap_port,
    smtpHost: account.smtp_host,
    smtpPort: account.smtp_port,
    encryptionType: account.encryption_type,
    smtpEncryption: String(account.smtp_encryption),
    emailId: account.id,
    forceImmediate: true,
    isForward: false,
    isReply: true,
    queueName: 'reply-queue',
    // 📎 Only metadata travels here; the cloud function pulls bytes from Storage.
    attachments: Array.isArray(attachments) ? attachments : [],
    sentByUserId: userId,
  };
};

// Keep a single forwarded message from blowing up the payload. Mirrors the
// classic inbox cap (10k) but a bit more generous for rich HTML bodies.
const MAX_BODY_CHARS = 50000;

const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object' && typeof value.toMillis === 'function') return value.toMillis();
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Renders ONE message's header + body for a forward. Unlike a reply (which
 * strips the nested chain), a forward keeps the full body so the recipient
 * sees exactly what was received.
 */
const renderForwardedMessage = (msg) => {
  if (!msg) return '';
  const when = new Date(msg.date || msg.timestamp || Date.now()).toLocaleString();
  const from = msg.from || 'Unknown';
  const to = msg.to || msg.recipients || msg.accountEmail || '';
  const subject = msg.subject || '(No subject)';

  const htmlBody = msg.content || msg.body || '';
  let body;
  if (htmlBody) {
    const trimmed = htmlBody.length > MAX_BODY_CHARS
      ? `${htmlBody.slice(0, MAX_BODY_CHARS)}<p style="color:#999;">[…gekürzt]</p>`
      : htmlBody;
    body = trimmed;
  } else {
    let plain = msg.bodyText || msg.preview || '';
    if (plain.length > MAX_BODY_CHARS) plain = `${plain.slice(0, MAX_BODY_CHARS)}\n\n[…gekürzt]`;
    body = `<div style="white-space: pre-wrap; word-break: break-word;">${textToHtml(plain)}</div>`;
  }

  return `
  <p style="margin: 0; color: #555;"><strong>From:</strong> ${escapeHtml(from)}</p>
  <p style="margin: 0; color: #555;"><strong>Date:</strong> ${escapeHtml(when)}</p>
  <p style="margin: 0; color: #555;"><strong>Subject:</strong> ${escapeHtml(subject)}</p>
  ${to ? `<p style="margin: 0; color: #555;"><strong>To:</strong> ${escapeHtml(to)}</p>` : ''}
  <div style="margin-top: 12px;">${body}</div>`;
};

/**
 * Builds the forwarded section for one OR many messages.
 *   - single message  → classic "---------- Forwarded message ----------" block
 *   - whole thread     → one header + every message (oldest first) stacked
 *
 * @param {object[]} messages messages to embed (already the user's chosen scope)
 * @returns {string} HTML block
 */
const buildForwardedSection = (messages) => {
  const list = (Array.isArray(messages) ? messages : []).filter(Boolean);
  if (list.length === 0) return '';

  if (list.length === 1) {
    return `
<div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #444;">
  <p style="margin: 0 0 10px; color: #888;">---------- Forwarded message ----------</p>
  ${renderForwardedMessage(list[0])}
</div>`;
  }

  // Whole thread: read top-to-bottom as a transcript (oldest first).
  const ordered = [...list].sort((a, b) => toMillis(a.timestamp || a.date) - toMillis(b.timestamp || b.date));
  const inner = ordered
    .map(renderForwardedMessage)
    .join('<hr style="border:none;border-top:1px solid #eee;margin:18px 0;" />');

  return `
<div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #444;">
  <p style="margin: 0 0 12px; color: #888;">---------- Forwarded conversation (${ordered.length} messages) ----------</p>
  ${inner}
</div>`;
};

/**
 * Builds the request payload for forwarding an email via `sendWorldEmail`.
 *
 * Mirrors the classic forward behaviour: `isForward: true` (the backend's
 * authoritative switch), `isReply: false`, `emailType: 'forward'`,
 * `leadId: null` (no CRM/status side effects), and `queueName:
 * 'world-email-queue'`. The recipient is an arbitrary, user-typed address — the
 * caller is responsible for guarding against forwarding back to the original
 * sender / the user's own mailbox.
 *
 * @param {object} args
 * @param {string} args.userId
 * @param {object} args.account sender account doc
 * @param {string} args.plainText optional intro note the user typed above the forward
 * @param {string} args.subject typically `Fwd: …`
 * @param {string[]} args.recipients the forward destination(s)
 * @param {object} [args.originalEmail] single message being forwarded (fallback)
 * @param {object[]} [args.forwardMessages] the chosen scope: [single] or whole thread
 * @param {object} [args.footerSettings]
 * @param {string|null} [args.footerId]
 * @param {Array<object>} [args.attachments] NEW uploads only (wire-format metadata)
 * @param {string[]} [args.cc]
 * @param {string[]} [args.bcc]
 * @returns {object} ready-to-POST request body
 */
export const buildForwardPayload = ({
  userId,
  account,
  plainText,
  subject,
  recipients,
  originalEmail,
  forwardMessages,
  footerSettings = {},
  footerId = null,
  attachments = [],
  cc = [],
  bcc = [],
}) => {
  // Use the explicit scope when provided; otherwise fall back to the single mail.
  const messages = (Array.isArray(forwardMessages) && forwardMessages.length > 0)
    ? forwardMessages
    : (originalEmail ? [originalEmail] : []);
  const htmlWithFooter = convertToHtml(plainText || '', footerSettings, footerId);
  const content = `${htmlWithFooter}\n\n${buildForwardedSection(messages)}`;

  return {
    recipients: recipients.filter(Boolean),
    cc: (Array.isArray(cc) ? cc : []).filter(Boolean),
    bcc: (Array.isArray(bcc) ? bcc : []).filter(Boolean),
    subject: subject || '',
    content,
    userId,
    businessName: account.sender_name || 'Forward',
    // No leadId on a forward — it must never touch a contact's status, and it
    // is explicitly NOT tied to the original sender's CRM record.
    leadId: null,
    emailType: 'forward',
    senderEmail: (account.email || '').trim(),
    senderPassword: account.password,
    senderName: account.sender_name || 'BonusClub',
    imapHost: account.imap_host,
    imapPort: account.imap_port,
    smtpHost: account.smtp_host,
    smtpPort: account.smtp_port,
    encryptionType: account.encryption_type,
    smtpEncryption: String(account.smtp_encryption),
    emailId: account.id,
    forceImmediate: true,
    isForward: true,
    isReply: false,
    queueName: 'world-email-queue',
    attachments: Array.isArray(attachments) ? attachments : [],
    sentByUserId: userId,
  };
};

export default buildReplyPayload;
